import { useState, Component, type ReactNode } from "react";
import type { CheckIn } from "../types";
import { TrendingDown, TrendingUp, Minus } from "lucide-react";

interface WeightPoint {
  week: string;
  weight: number;
  label: string;
}

// Error boundary so chart issues never crash the whole page
class ChartErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Weight Trend</h3>
          <p className="text-sm text-gray-400 text-center py-4">Unable to display weight chart</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function parseDateStr(dateStr: string): { day: number; month: number } {
  // Normalize: if already ISO with T, use as-is; if just YYYY-MM-DD, append T00:00:00Z
  let raw = dateStr;
  if (!dateStr.includes("T")) raw = dateStr + "T00:00:00Z";
  // Strip trailing Z if there's already a timezone offset
  const d = new Date(raw);
  if (isNaN(d.getTime())) return { day: NaN, month: NaN };
  return { day: d.getUTCDate(), month: d.getUTCMonth() };
}

function buildWeightData(checkins: CheckIn[], weeks: number): WeightPoint[] {
  const sorted = [...checkins]
    .filter((c) => c.current_weight != null && c.current_weight !== undefined && c.week_start_date)
    .sort((a, b) => (a.week_start_date! < b.week_start_date! ? -1 : 1));

  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - weeks * 7);
  const cutoffStr = cutoff.toISOString().split("T")[0];

  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  return sorted
    .filter((c) => {
      const dateOnly = c.week_start_date!.split("T")[0];
      return dateOnly >= cutoffStr;
    })
    .map((c) => {
      const weight = typeof c.current_weight === "string" ? parseFloat(c.current_weight) : Number(c.current_weight);
      if (isNaN(weight)) return null;
      const { day, month } = parseDateStr(c.week_start_date!);
      const isValid = !isNaN(day) && !isNaN(month);
      return {
        week: c.week_start_date!,
        weight,
        label: isValid ? `${day} ${months[month]}` : c.week_start_date!.split("T")[0],
      };
    })
    .filter((p): p is WeightPoint => p !== null);
}

/** Simple SVG line chart — no external dependencies, can't crash */
function SimpleLineChart({ data }: { data: WeightPoint[] }) {
  const padding = { top: 20, right: 16, bottom: 28, left: 40 };
  const width = 100; // viewBox units (percentage-like)
  const height = 50;
  const plotW = width - padding.left - padding.right;
  const plotH = height - padding.top - padding.bottom;

  const weights = data.map((d) => Number(d.weight)).filter((w) => !isNaN(w));
  if (weights.length === 0) return null;
  const min = Math.min(...weights);
  const max = Math.max(...weights);
  const range = max - min || 1;
  const yMin = min - range * 0.1;
  const yMax = max + range * 0.1;

  const points = data.map((d, i) => {
    const x = padding.left + (data.length === 1 ? plotW / 2 : (i / (data.length - 1)) * plotW);
    const y = padding.top + plotH - ((d.weight - yMin) / (yMax - yMin)) * plotH;
    return { x, y, ...d };
  });

  const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");

  // Y-axis tick marks
  const yTicks = 4;
  const yTickValues = Array.from({ length: yTicks + 1 }, (_, i) => yMin + (i / yTicks) * (yMax - yMin));

  // X-axis labels — show max ~6 labels
  const maxLabels = 6;
  const step = Math.max(1, Math.floor(data.length / maxLabels));
  const xLabels = points.filter((_, i) => i % step === 0 || i === points.length - 1);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      {/* Grid lines */}
      {yTickValues.map((v, i) => {
        const y = padding.top + plotH - ((v - yMin) / (yMax - yMin)) * plotH;
        return (
          <g key={i}>
            <line x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke="#f0f0f0" strokeWidth="0.3" />
            <text x={padding.left - 1} y={y + 1} textAnchor="end" fontSize="2.5" fill="#999">
              {v.toFixed(0)}
            </text>
          </g>
        );
      })}

      {/* X-axis labels */}
      {xLabels.map((p, i) => (
        <text key={i} x={p.x} y={height - 2} textAnchor="middle" fontSize="2.5" fill="#999">
          {p.label}
        </text>
      ))}

      {/* Line */}
      <path d={pathD} fill="none" stroke="#5f1b2c" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" />

      {/* Dots */}
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="1.2" fill="#5f1b2c" />
      ))}
    </svg>
  );
}

export function WeightTrendChart({ checkins }: { checkins: CheckIn[] }) {
  const [range, setRange] = useState<number>(26);
  const data = buildWeightData(checkins, range);

  if (data.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Weight Trend</h3>
        </div>
        <p className="text-sm text-gray-400 text-center py-8">No weight data recorded yet</p>
      </div>
    );
  }

  const first = data[0].weight;
  const current = data[data.length - 1].weight;
  const change = current - first;

  return (
    <ChartErrorBoundary>
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Weight Trend</h3>
          <div className="flex gap-1">
            {[
              { n: 4, label: "4W" },
              { n: 12, label: "12W" },
              { n: 26, label: "All" },
            ].map(({ n, label }) => (
              <button key={n} onClick={() => setRange(n)} className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${range === n ? "bg-[#5f1b2c] text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>{label}</button>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <p className="text-[11px] text-gray-400 uppercase tracking-wider">Starting</p>
            <p className="text-lg font-bold text-gray-900">{first.toFixed(1)} <span className="text-xs font-normal text-gray-400">kg</span></p>
          </div>
          <div>
            <p className="text-[11px] text-gray-400 uppercase tracking-wider">Current</p>
            <p className="text-lg font-bold text-gray-900">{current.toFixed(1)} <span className="text-xs font-normal text-gray-400">kg</span></p>
          </div>
          <div>
            <p className="text-[11px] text-gray-400 uppercase tracking-wider">Change</p>
            <p className={`text-lg font-bold flex items-center gap-1 ${change < 0 ? "text-emerald-600" : change > 0 ? "text-red-500" : "text-gray-900"}`}>
              {change < 0 ? <TrendingDown className="w-4 h-4" /> : change > 0 ? <TrendingUp className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
              {change >= 0 ? "+" : ""}{change.toFixed(1)}
              <span className="text-xs font-normal text-gray-400">kg</span>
            </p>
          </div>
        </div>

        {/* Chart */}
        <div className="h-48">
          <SimpleLineChart data={data} />
        </div>
      </div>
    </ChartErrorBoundary>
  );
}